import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as ChevronUp } from "../../images/chevron-up.svg";
import { ReactComponent as ChevronDown } from "../../images/chevron-down.svg";
import "./dropdown.scss";
import Tooltip from "../tooltip/tooltip.tsx";

export interface DropdownItems {
  id: string;
  name: string;
  lookupType?: string;
  legacyId?: string;
  description?: string;
  deselectOption?: string;
}

interface DropdownProps {
  items: DropdownItems[];
  defaultOption?: string;
  onChange?: (item: DropdownItems | string) => void;
  noResultsMessage?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  allowDeselect?: boolean;
  deselectOption?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  defaultOption = "Choose one",
  onChange,
  noResultsMessage,
  disabled = false,
  className,
  allowDeselect = false,
  deselectOption,
}) => {
  const [selectedItem, setSelectedItem] = useState<DropdownItems | string>(""); // Initialize as empty string
  const [isActive, setIsActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClick = (item: DropdownItems | string) => {
    setSelectedItem(item);
    setIsActive(false);
    if (onChange) {
      onChange(item);
    }
  };

  // Create a deselect option if allowDeselect is true
  const deselectItem: DropdownItems | null = allowDeselect
    ? { id: "deselect", name: deselectOption }
    : null;

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedItems = [deselectItem, ...filteredItems].filter(Boolean);

  const exactMatch = filteredItems.some(
    (item) => item.name.toLowerCase() === searchTerm.toLowerCase()
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={`dropdown ${className} ${disabled ? "disabled" : ""}`}
      style={{ width: "90%" }}
    >
      <Tooltip
        content={
          selectedItem && typeof selectedItem !== "string"
            ? selectedItem.name
            : defaultOption
        }
        direction="top"
        visible={
          (selectedItem && typeof selectedItem !== "string"
            ? selectedItem.name.length
            : defaultOption.length) > 100
        }
      >
        <div
          onClick={() => !disabled && setIsActive(!isActive)}
          className="dropdown-btn"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsActive(!isActive);
            }
          }}
        >
          <div className="dropdown-input">
            {typeof selectedItem === "string" && selectedItem === ""
              ? defaultOption
              : selectedItem?.name}
          </div>
          <span>{isActive ? <ChevronUp /> : <ChevronDown />}</span>
        </div>
      </Tooltip>
      {isActive && !disabled && (
        <div
          className="dropdown-content"
          style={{ display: isActive ? "block" : "none" }}
        >
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dropdown-search"
            disabled={disabled}
            tabIndex={0}
          />
          {searchTerm && !exactMatch && noResultsMessage && (
            <div className="no-results">{noResultsMessage}</div>
          )}
          {displayedItems.map((item, index) => (
            <div
              key={item.id || index} 
              className={`item ${
                selectedItem?.name === item.name ? "item-selected" : ""
              }`}
              onClick={() => handleClick(item.id === "deselect" ? "" : item)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleClick(item.id === "deselect" ? "" : item);
                }
              }}
            >
              {item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
